import React, { useContext, useState, ContextType } from "react";
import { Context } from "..";
import { MdDelete, MdEdit } from "react-icons/md";
import Modal from "./Modal";
import Input from "./Input";
import { useTranslation } from "react-i18next";

type Transaction = ContextType<typeof Context>["transactions"][0][0];

const formatter = new Intl.NumberFormat("en", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

type TableProps = {
  transactions?: Transaction[] | null;
};

const Table: React.FC<TableProps> = ({ transactions: pushedTransactions }) => {
  const {
    transactions: contextTransactions,
    expenseCategories,
    incomeCategories,
    events,
  } = useContext(Context);

  const transactions = pushedTransactions ?? contextTransactions[0];

  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [
    transactionToUpdate,
    setTransactionToUpdate,
  ] = useState<Transaction | null>(null);
  const totalPages = Math.ceil(transactions.length / itemsPerPage);

  const currentPageTransactions = transactions
    .sort((a, b) => (new Date(a.date) > new Date(b.date) ? -1 : 1))
    .slice(
      Math.min(currentPage, totalPages - 1) * itemsPerPage,
      itemsPerPage * Math.min(currentPage, totalPages - 1) + itemsPerPage
    );

  const { t } = useTranslation();

  return (
    <>
      {transactionToUpdate && (
        <Modal
          onClose={() => setTransactionToUpdate(null)}
          className="w-11/12 sm:w-10/12 md:w-9/12 lg:w-9/12 xl:w-6/12"
        >
          <div className="p-10">
            <Input
              onClose={() => setTransactionToUpdate(null)}
              updatingTransaction={transactionToUpdate}
            />
          </div>
        </Modal>
      )}
      <div className="p-5 bg-gray-50">
        <div className="mb-8">
          {t("Items per page")}:
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
            className="ml-4 rounded-md border border-gray-300 px-4 py-2"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <table>
          <thead>
            <tr>
              <th>
                <div className="pl-2">{t("Date")}</div>
              </th>
              <th>
                {t("Category")} / {t("Event")}
              </th>
              <th>
                {t("Income")} / {t("Expense")}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {currentPageTransactions.map((t) => {
              const category = [
                ...expenseCategories[0],
                ...incomeCategories[0],
              ].find((types) => types.id === t.attachedTo.id);

              const categoryName =
                category !== undefined
                  ? category.label
                  : events[0].find((e) => e.id === t.attachedTo.id)?.name;

              return (
                <tr className="rounded-xl mb-4" key={t.id}>
                  <td className="p-2">
                    <div className="p-2">{t.date.toLocaleString()}</div>
                  </td>
                  <td className="p-2">
                    <div className="p-2">{categoryName ?? "-"}</div>
                  </td>
                  <td
                    className={t.value < 0 ? "text-red-500" : "text-green-600"}
                  >
                    {formatter.format(t.value)}
                  </td>
                  <td>
                    <div className="flex">
                      <div
                        onClick={() => {
                          setTransactionToUpdate(t);
                        }}
                        className="h-8 w-8 mr-2 flex items-center justify-center rounded-full text-gray-600 cursor-pointer hover:bg-gray-200"
                      >
                        <MdEdit className="text-blue-500" />
                      </div>
                      <div
                        className="h-8 w-8 flex items-center justify-center rounded-full text-gray-600 cursor-pointer hover:bg-gray-200"
                        onClick={() =>
                          contextTransactions[1]((transactions) =>
                            transactions.filter((tr) => tr.id !== t.id)
                          )
                        }
                      >
                        <MdDelete className="text-red-500" />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
            {transactions.length === 0 && (
              <tr>
                <td colSpan={3}>
                  <div className="text-center text-gray-400">
                    {t("No table items")}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="mt-10">
            <button
              className="px-10 rounded-md border border-gray-300"
              onClick={() =>
                setCurrentPage((p) => Math.max(0, Math.min(p, totalPages) - 1))
              }
            >
              Previous
            </button>
            <span className="mx-4">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              className="px-10 rounded-md border border-gray-300"
              onClick={() =>
                setCurrentPage((p) => (p + 2 > totalPages ? p : p + 1))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Table;
